type ReportsTab = 'overview' | 'categories' | 'trends';

export function buildReportsPdfFilename(args: { month: string; tab: ReportsTab }): string {
  const { month, tab } = args;
  return `Velora_Report_${month}_${tab}.pdf`;
}

type ExportPageSize = 'a4' | 'letter';

const EXPORT_EXPAND_SELECTOR = '[data-export-expand="true"]';

const loadImage = (dataUrl: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load captured image for PDF export.'));
    img.src = dataUrl;
  });

const expandExportContainers = (root: HTMLElement): Array<() => void> => {
  const nodes = Array.from(root.querySelectorAll<HTMLElement>(EXPORT_EXPAND_SELECTOR));
  return nodes.map((node) => {
    const prevMaxHeight = node.style.maxHeight;
    const prevOverflow = node.style.overflow;
    const prevOverflowY = node.style.overflowY;
    node.style.maxHeight = 'none';
    node.style.overflow = 'visible';
    node.style.overflowY = 'visible';
    return () => {
      node.style.maxHeight = prevMaxHeight;
      node.style.overflow = prevOverflow;
      node.style.overflowY = prevOverflowY;
    };
  });
};

export async function exportReportsViewToPdf(args: {
  node: HTMLElement;
  filename: string;
  page?: ExportPageSize;
  pixelRatio?: number;
}): Promise<void> {
  const { node, filename, page = 'a4', pixelRatio = 2 } = args;
  if (!node) throw new Error('Unable to export report: missing export container.');

  const restorers = expandExportContainers(node);

  try {
    const [{ toPng }, { jsPDF }] = await Promise.all([import('html-to-image'), import('jspdf')]);
    const imageDataUrl = await toPng(node, {
      pixelRatio,
      backgroundColor: '#ffffff',
      cacheBust: true,
    });
    const image = await loadImage(imageDataUrl);

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: page,
      compress: true,
    });

    const margin = 24;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const printableWidth = pageWidth - margin * 2;
    const printableHeight = pageHeight - margin * 2;

    const scale = printableWidth / image.width;
    const sourceSliceHeightPx = Math.max(1, Math.floor(printableHeight / scale));
    let sourceOffsetY = 0;
    let pageIndex = 0;

    while (sourceOffsetY < image.height) {
      const remaining = image.height - sourceOffsetY;
      const sliceHeightPx = Math.min(sourceSliceHeightPx, remaining);
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = image.width;
      sliceCanvas.height = sliceHeightPx;
      const ctx = sliceCanvas.getContext('2d');
      if (!ctx) throw new Error('Unable to render report image for PDF export.');

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
      ctx.drawImage(
        image,
        0,
        sourceOffsetY,
        image.width,
        sliceHeightPx,
        0,
        0,
        image.width,
        sliceHeightPx
      );

      const pageImageData = sliceCanvas.toDataURL('image/png');
      const renderedHeight = sliceHeightPx * scale;

      if (pageIndex > 0) doc.addPage();
      doc.addImage(pageImageData, 'PNG', margin, margin, printableWidth, renderedHeight, undefined, 'FAST');

      sourceOffsetY += sliceHeightPx;
      pageIndex += 1;
    }

    doc.save(filename);
  } finally {
    restorers.forEach((restore) => restore());
  }
}

